import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Layers, RotateCcw, Plus, TrendingUp, TrendingDown,
  Wand2, List, Check, X, Sparkles, ChevronLeft,
} from "lucide-react";

interface Flashcard {
  id: string;
  type: "definition" | "fill_blank" | "multiple_choice" | "concept" | "true_false";
  front: string;
  back: string;
  options?: string[] | null;
  difficulty: "easy" | "medium" | "hard";
}

interface FlashcardsViewProps {
  skillNodeId: string;
  courseId?: string;
  nodeTitle: string;
}

const GREEN_DEEP = "#1C3829";
const LIME = "hsl(65,65%,75%)";

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

type ViewMode = "empty" | "study" | "complete" | "list";

// ── Difficulty pill ────────────────────────────────────────────────────────────
function DifficultyPill({ d }: { d: Flashcard["difficulty"] }) {
  const cls =
    d === "easy"
      ? "bg-green-500/15 text-green-700 dark:text-green-300"
      : d === "hard"
      ? "bg-red-500/15 text-red-700 dark:text-red-300"
      : "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return (
    <span style={{ fontSize: 10, borderRadius: 6, padding: "1px 8px", fontWeight: 500 }} className={cls}>
      {d}
    </span>
  );
}

function typeLabel(t: Flashcard["type"]) {
  return t === "multiple_choice" ? "MCQ"
    : t === "fill_blank" ? "Fill-in"
    : t === "true_false" ? "T/F"
    : t === "definition" ? "Define"
    : "Concept";
}

// ── AI Toolbar ────────────────────────────────────────────────────────────────
function AIToolbar({
  onApply,
  generating,
}: {
  onApply: (op: string, custom?: string) => void;
  generating: boolean;
}) {
  const [customInput, setCustomInput] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  return (
    <div style={{
      borderTop: "1px solid hsl(var(--border))",
      background: "hsl(var(--background))",
      padding: "10px 0 14px",
      flexShrink: 0,
    }}>
      <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 8, fontWeight: 500 }}>
        AI tools
      </p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Button size="sm" variant="outline" disabled={generating} onClick={() => onApply("add_more")}
          style={{ gap: 5, fontSize: 12 }}>
          <Plus style={{ width: 12, height: 12 }} /> Add 5 more
        </Button>
        <Button size="sm" variant="outline" disabled={generating} onClick={() => onApply("make_harder")}
          style={{ gap: 5, fontSize: 12 }}>
          <TrendingUp style={{ width: 12, height: 12 }} /> Make harder
        </Button>
        <Button size="sm" variant="outline" disabled={generating} onClick={() => onApply("make_easier")}
          style={{ gap: 5, fontSize: 12 }}>
          <TrendingDown style={{ width: 12, height: 12 }} /> Make easier
        </Button>
        <Button size="sm" variant="outline" disabled={generating} onClick={() => setShowCustom(s => !s)}
          style={{ gap: 5, fontSize: 12 }}>
          <Wand2 style={{ width: 12, height: 12 }} /> Custom
        </Button>
      </div>
      {showCustom && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-end" }}>
          <Textarea
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            placeholder='e.g. "Focus more on applications" or "Add more fill-in-the-blank cards"'
            rows={2}
            style={{ flex: 1, resize: "none", fontSize: 13 }}
          />
          <Button
            size="sm"
            disabled={!customInput.trim() || generating}
            onClick={() => { onApply("custom", customInput); setCustomInput(""); setShowCustom(false); }}
            style={{ background: GREEN_DEEP, color: LIME }}
          >
            {generating ? "…" : "Apply"}
          </Button>
        </div>
      )}
      {generating && (
        <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 8 }}>
          Generating…
        </p>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FlashcardsView({ skillNodeId, courseId, nodeTitle }: FlashcardsViewProps) {
  const [deck, setDeck]       = useState<Flashcard[]>([]);
  const [queue, setQueue]     = useState<string[]>([]);    // card IDs remaining this round
  const [knownIds, setKnownIds] = useState<Set<string>>(new Set());
  const [flipped, setFlipped] = useState(false);
  const [view, setView]       = useState<ViewMode>("empty");
  const [generating, setGenerating] = useState(false);

  const currentCard = queue.length > 0 ? deck.find(c => c.id === queue[0]) ?? null : null;
  const remainingCards = queue.length;
  const knownCount = knownIds.size;
  const totalCards = deck.length;
  const progress = totalCards > 0 ? ((totalCards - remainingCards) / totalCards) * 100 : 0;

  // ── Edge function call ──────────────────────────────────────────────────────
  const callFlashcard = useCallback(async (
    operation: string,
    opts?: { existingDeck?: Flashcard[]; customInstruction?: string; count?: number }
  ): Promise<Flashcard[]> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");

    const { data, error } = await supabase.functions.invoke("course-assistant", {
      body: {
        messages: [],
        context: {
          courseId: courseId ?? null,
          courseName: null,
          nodeId: skillNodeId,
          nodeName: nodeTitle,
          pageType: "learner",
        },
        mode: "flashcard",
        operation,
        existingDeck:        opts?.existingDeck ?? [],
        customInstruction:   opts?.customInstruction ?? "",
        count:               opts?.count ?? 12,
        _authToken:          session.access_token,
      },
    });

    if (error) throw new Error(typeof error === "string" ? error : (error as any)?.message || JSON.stringify(error));
    console.log("[FlashcardsView] response:", data);
    const cards = (data?.cards ?? []) as Flashcard[];
    return cards.map(c => ({ ...c, id: c.id || uid() }));
  }, [skillNodeId, courseId, nodeTitle]);

  // ── Session helpers ─────────────────────────────────────────────────────────
  const startStudying = (cards: Flashcard[]) => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setQueue(shuffled.map(c => c.id));
    setKnownIds(new Set());
    setFlipped(false);
    setView("study");
  };

  const generateDeck = async () => {
    setGenerating(true);
    try {
      const cards = await callFlashcard("generate");
      if (!cards.length) {
        toast.error("No cards generated", { description: "The AI couldn't generate cards — make sure this node has lesson content." });
        return;
      }
      setDeck(cards);
      startStudying(cards);
    } catch (e: any) {
      console.error("Flashcard generation error:", e);
      toast.error("Failed to generate flashcards", { description: e?.message || "Unknown error" });
    } finally {
      setGenerating(false);
    }
  };

  const applyAI = async (operation: string, customInstruction?: string) => {
    setGenerating(true);
    try {
      if (operation === "add_more") {
        const newCards = await callFlashcard("add_more", { existingDeck: deck, count: 5 });
        if (!newCards.length) { toast.error("No new cards generated"); return; }
        const updatedDeck = [...deck, ...newCards];
        setDeck(updatedDeck);
        setQueue(prev => [...prev, ...newCards.map(c => c.id)]);
        if (view === "complete") startStudying(newCards);
      } else {
        const newCards = await callFlashcard(operation, { existingDeck: deck, customInstruction });
        if (!newCards.length) { toast.error("No cards returned"); return; }
        setDeck(newCards);
        startStudying(newCards);
      }
    } catch (e: any) {
      console.error("AI tool error:", e);
      toast.error("AI tool failed", { description: e?.message || "Unknown error" });
    } finally {
      setGenerating(false);
    }
  };

  // ── Card actions ────────────────────────────────────────────────────────────
  const flipCard = () => setFlipped(f => !f);

  const markKnown = () => {
    if (!currentCard) return;
    setKnownIds(prev => new Set([...prev, currentCard.id]));
    setQueue(prev => {
      const next = prev.slice(1);
      if (next.length === 0) setView("complete");
      return next;
    });
    setFlipped(false);
  };

  const markReview = () => {
    if (!currentCard) return;
    setQueue(prev => [...prev.slice(1), prev[0]]);
    setFlipped(false);
  };

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (view === "empty") {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "calc(100vh - 100px)", gap: 24, padding: "0 16px",
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: 36,
          background: GREEN_DEEP, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Layers style={{ width: 32, height: 32, color: LIME }} />
        </div>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Study with Flashcards</h2>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, lineHeight: 1.6 }}>
            AI will generate a personalised deck from the <strong>{nodeTitle}</strong> lesson
            material, course objectives, and key concepts.
          </p>
        </div>
        <Button
          onClick={generateDeck} disabled={generating} size="lg"
          style={{ background: GREEN_DEEP, color: LIME, gap: 8 }}
        >
          <Sparkles style={{ width: 16, height: 16 }} />
          {generating ? "Generating deck…" : "Generate Deck"}
        </Button>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  if (view === "list") {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "20px 16px 8px", display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexShrink: 0 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{deck.length} cards in deck</h2>
          <Button variant="outline" size="sm" onClick={() => setView("study")} style={{ gap: 5 }}>
            <ChevronLeft style={{ width: 13, height: 13 }} />
            Back to studying
          </Button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingBottom: 8 }}>
          {deck.map((card, i) => (
            <div
              key={card.id}
              style={{
                borderRadius: 12, border: "1px solid hsl(var(--border))",
                padding: "14px 16px", background: "hsl(var(--card))",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>#{i + 1}</span>
                <Badge variant="outline" style={{ fontSize: 10, padding: "0 6px" }}>{typeLabel(card.type)}</Badge>
                <DifficultyPill d={card.difficulty} />
                {knownIds.has(card.id) && (
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "hsl(142, 76%, 36%)" }}>✓ Known</span>
                )}
              </div>
              <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>{card.front}</p>
              <p style={{
                fontSize: 13, color: "hsl(var(--muted-foreground))",
                paddingTop: 6, borderTop: "1px dashed hsl(var(--border))",
                whiteSpace: "pre-wrap",
              }}>
                {card.back}
              </p>
            </div>
          ))}
        </div>

        <AIToolbar onApply={applyAI} generating={generating} />
      </div>
    );
  }

  // ── Complete state ──────────────────────────────────────────────────────────
  if (view === "complete") {
    const pct = totalCards > 0 ? Math.round((knownCount / totalCards) * 100) : 0;
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "calc(100vh - 100px)", gap: 20, padding: "0 16px",
      }}>
        <div style={{ fontSize: 52 }}>🎉</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, textAlign: "center" }}>Round complete!</h2>
        <p style={{ color: "hsl(var(--muted-foreground))", textAlign: "center" }}>
          You knew <strong>{knownCount} of {totalCards}</strong> cards ({pct}%)
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <Button onClick={() => startStudying(deck)} style={{ background: GREEN_DEEP, color: LIME, gap: 6 }}>
            <RotateCcw style={{ width: 14, height: 14 }} />
            Study all again
          </Button>
          {knownCount < totalCards && (
            <Button variant="outline" onClick={() => startStudying(deck.filter(c => !knownIds.has(c.id)))}>
              Study missed ({totalCards - knownCount})
            </Button>
          )}
          <Button variant="outline" onClick={() => setView("list")} style={{ gap: 5 }}>
            <List style={{ width: 13, height: 13 }} />
            View deck
          </Button>
        </div>
        <div style={{ width: "100%", maxWidth: 560, borderTop: "1px solid hsl(var(--border))", paddingTop: 12 }}>
          <AIToolbar onApply={applyAI} generating={generating} />
        </div>
      </div>
    );
  }

  // ── Study view ──────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 100px)",
      maxWidth: 680, margin: "0 auto",
      padding: "16px 16px 0",
    }}>

      {/* Progress header */}
      <div style={{ flexShrink: 0, marginBottom: 12 }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 6, fontSize: 12, color: "hsl(var(--muted-foreground))",
        }}>
          <span>{remainingCards} remaining · {knownCount} known</span>
          <button
            onClick={() => setView("list")}
            style={{
              background: "none", border: "none", cursor: "pointer", fontSize: 12,
              color: "hsl(var(--muted-foreground))", display: "flex", alignItems: "center", gap: 4,
            }}
          >
            <List style={{ width: 11, height: 11 }} />
            View all
          </button>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: "hsl(var(--muted))", overflow: "hidden" }}>
          <div style={{
            height: "100%", width: `${progress}%`,
            background: GREEN_DEEP, borderRadius: 2, transition: "width 0.3s ease",
          }} />
        </div>
      </div>

      {currentCard && (
        <>
          {/* Type + difficulty */}
          <div style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <Badge variant="outline" style={{ fontSize: 11 }}>{typeLabel(currentCard.type)}</Badge>
            <DifficultyPill d={currentCard.difficulty} />
          </div>

          {/* Card flip */}
          <div
            className="flashcard-scene"
            style={{ flex: 1, minHeight: 0, position: "relative", cursor: flipped ? "default" : "pointer" }}
            onClick={!flipped ? flipCard : undefined}
          >
            <div
              className={`flashcard-inner${flipped ? " is-flipped" : ""}`}
              style={{ position: "absolute", inset: 0 }}
            >
              {/* Front */}
              <div
                className="flashcard-face"
                style={{
                  borderRadius: 16,
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--card))",
                  padding: "32px 28px",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                }}
              >
                <p style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.55, textAlign: "center", whiteSpace: "pre-wrap" }}>
                  {currentCard.front}
                </p>
                <p style={{ marginTop: 20, fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
                  Tap to reveal answer
                </p>
              </div>

              {/* Back */}
              <div
                className="flashcard-face back"
                style={{
                  borderRadius: 16,
                  border: `2px solid ${GREEN_DEEP}`,
                  background: "hsl(var(--card))",
                  padding: "32px 28px",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                  overflowY: "auto",
                }}
              >
                <p style={{ fontSize: 15, lineHeight: 1.7, textAlign: "center", whiteSpace: "pre-wrap" }}>
                  {currentCard.back}
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ flexShrink: 0, display: "flex", gap: 10, paddingTop: 12, paddingBottom: 4 }}>
            {!flipped ? (
              <Button
                onClick={flipCard}
                style={{ flex: 1, background: GREEN_DEEP, color: LIME }}
              >
                Flip card
              </Button>
            ) : (
              <>
                <Button onClick={markReview} variant="outline" style={{ flex: 1, gap: 6 }}>
                  <X style={{ width: 15, height: 15 }} />
                  Study again
                </Button>
                <Button onClick={markKnown} style={{ flex: 1, background: GREEN_DEEP, color: LIME, gap: 6 }}>
                  <Check style={{ width: 15, height: 15 }} />
                  Got it
                </Button>
              </>
            )}
          </div>
        </>
      )}

      <AIToolbar onApply={applyAI} generating={generating} />
    </div>
  );
}
